<template>
    <div class="sidebar" :class="{ 'sidebar--floating' : isFloating }" :style="{ 'padding-top': offsetTop + 'px' }" >

        <loading v-if="isLoading" :alt="true"></loading>

        <template v-else>

            <sidebar-header :user="user" :disableMenu="disableUserMenu" ></sidebar-header>

            <div class="sidebar__options" v-if="showOptions">

                <h2 class="sidebar__tag" :class="{ 'sidebar__tag--rounded' : isFloating }" v-if="backLink">
                    <router-link :to="backLink" class="sidebar__back button-backward button-backward-primary" >{{ backLinkTitle }}</router-link>
                    <router-link :to="collectionLink" class="sidebar__name" :class="{ 'is-active': isCollectionActive }" v-if="collectionName" @click.native.prevent.meta="$emit('viewCollectionInfo')">
                        <span class="mini-hidden" v-if="collectionPrefix">{{ collectionPrefix }}</span> {{ collectionNameExcerpt }}
                    </router-link>
                </h2>

                <h2 class="sidebar__tag sidebar__tag--inverted sidebar__tag--multi"  :class="{ 'sidebar__tag--rounded' : isFloating }" v-if="addLink">
                    <router-link :to="addLink" class="sidebar__add-new"><span class="icon icon--add icon--inline"></span> Nieuw</router-link>
                </h2>

                <slot name="options"></slot>

            </div>

            <div class="sidebar__content scrollable" id="sidebar-content" v-scroller>
                <slot>
                    <timeline :items="items" :isActive="isActive" :collectionName="collectionName" v-if="!isFloating">
                        <template slot="item" scope="props"><slot name="item" v-bind="props"></slot></template>
                    </timeline>
                </slot>
            </div>


            <div class="sidebar__copyright"><a href="#" @click.prevent="showDisclaimer">&copy; {{ currentYear }} <span class="mini-hidden">Kiempunt - disclaimer</span></a></div>

        </template>

    </div>
</template>


<script>

import SidebarHeader from 'js/components/sidebar/SidebarHeader.vue'
import Timeline from 'js/admin/components/sidebar/Timeline.vue'
import { excerpt } from 'js/helpers/transformers'
import { vueScrollDirective } from 'js/helpers/scroll'

export default {
    components: { SidebarHeader, Timeline },
    props: {
        user: {},
        collectionPrefix: {
            type: String,
            default: '',
        },
        collectionName: {
            type: String,
            default: '',
        },
        collectionLink: {
            default: () => ({}),
        },
        addLink: { },
        backLink: { },
        backLinkTitle: {
            type: String,
            default: 'Dashboard'
        },
        showOptions: {
            type: Boolean,
            default: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        disableUserMenu: {
            type: Boolean,
            default: false,
        },

        isFloating: {
            type: Boolean,
            default: false,
        },

        // convenience for Timeline
        items: {
            type: Array,
            default: () => [],
        },
        isActive: {
            type: Function,
            default: () => false,
        },
        isCollectionActive: {
            type: Boolean,
            default: false,
        },
    },

    data: function() {
        return {
            offsetTop: 0,
        }
    },

    vuex: {
        actions: {
            showDisclaimer: ({ dispatch }) => {
                dispatch('SHOW_MODAL', 'legal', { type: 'disclaimer', title: 'Disclaimer'})
            },
        }
    },

    computed: {
        collectionNameExcerpt: function() {
            return excerpt(this.collectionName, 20)
        },

        currentYear: function() {
            return new Date().getFullYear()
        },
    },

    directives: {
        scroller: vueScrollDirective
    },

    mounted: function() {
        // make sure sidebar top is aligned with parent
        const mainView = document.getElementById('main-view')
        if (mainView) {
            this.offsetTop = mainView.offsetTop
        }
    }

}
</script>



<style lang="scss">

    @import 'global';

    .sidebar {
        display: flex;
        flex-direction: column;
        height: 100%;
        max-height: 100%;

        @include gradient();
        color: $color-typography-inverted;

        .isTouchEnabled & {
            // prevents white bottom part when scrolling on iPad and browser menu disappears
            -webkit-backface-visibility: hidden;
            transform: translate3d(0, 0, 0);
            min-height: 100vh;
            max-height: auto;

            // make sure user can scroll to bottom
            .sidebar__content > * {
                padding-bottom: 2em;
            }
        }

        &--floating {
            height: auto;
            background: transparent;
        }

        &__header, &__options {
            flex-grow: 0;
            flex-shrink: 0;
            flex-basis: auto;
        }

        &__content {
            flex-grow: 1;
            flex-shrink: 1;
            flex-basis: auto;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
        }

        &__copyright {
          .sidebar__wrap--teacher .sidebar--student &, .sidebar--floating & {
            display: none;
          }

          font-size: 0.68em;
          padding: 0.15em 0;
          text-align: center;
          color: $color-dark;
          white-space: nowrap;

          a {
            opacity: 0.9;
            color: $color-dark;
            &:hover {
              opacity: 1;
              color: $color-typography;
            }
          }
        }

        &__tag {
            margin: 0;
            line-height: 28px;
            @include tagify();
            @include background-opacity($color-tag);
            @include clearfix();

            a {
                display: block;
                color: lighten($color-primary, 15%);

                padding: 0.3em 1em;

                text-decoration: none;

                &:hover, &:active, &.active {
                    color: $color-typography-inverted;
                }

            }

            &--multi {
                text-align: center;
                a {
                    display: inline-block;
                }
            }

            &--inverted {
                background-color: $color-white;
                background: transparentize($color-white, 0.8);
                color: darken($color-primary, 10%);
                a {
                    color: darken($color-primary, 10%);
                    &.active, &:hover {
                        color: $color-typography-inverted;
                    }

                }
            }

            &--rounded {
                border-top-right-radius: 4px;
                border-bottom-right-radius: 4px;
            }

        }

        &__back {
            float: left;
        }

        &__name {
            float: right;
            padding: 0.3em 1em;
            display: inline-block;
            &.is-active, &.router-link-exact-active {
                color: $color-tag;
                background-color: $color-white;
                border-top-left-radius: 4px;
                border-bottom-left-radius: 4px;

                &:hover, &:active {
                    color: $color-tag;
                }
            }
        }

        &__content {
            width: 100%;
        }

        &__empty {
            color: $color-typography-inverted;
            color: rgba($color-typography-inverted, 0.9);
            padding: 2em;
        }

    }

</style>



// WEBPACK FOOTER //
// Sidebar.vue?615ac616